import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import SelectLanguage from "../../../common/SelectLanguage";
import CloseIcon from "@mui/icons-material/Close";

import CAUTIONICON from "../../../assets/icons/caution.webp";
import GOODICON from "../../../assets/icons/good.webp";
import GOODICON1 from "../../../assets/icons/GOODICON1.webp";
import MARKERICON from "../../../assets/icons/marker.webp";
import WARNINGICON from "../../../assets/icons/warning.webp";
import { CustomSlider } from "../../../common";

const SuggestedMarker = ({ suggestedValue }) => (
  <Box
    sx={{
      position: "absolute",
      top: "18px",
      left: `${suggestedValue}%`,
      pointerEvents: "none",
      transform: "translateX(-50%)",
    }}
  >
    <Box component="img" src={MARKERICON} height={30} />
  </Box>
);

const marks = [
  { value: 0, label: "0%" },
  { value: 20, label: "20%" },
  { value: 40, label: "40%" },
  { value: 60, label: "60%" },
  { value: 80, label: "80%" },
  { value: 100, label: "100%" },
];

const SelectLanguageModal = ({
  actionLoading,
  open,
  onClose,
  onSelect,
  onConfirm,
  passingCriteriaSet,
  setPassingCriteriaSet,
  setInterviewValue,
  interviewValue,
  setShowLanguageSelectModal,
  showLanguageSelectModal,
}) => {
  const [suggestedValue] = useState(70);

  // Function to handle the message text based on the value
  const getMessageForValue = (value) => {
    if (value < 40) {
      return "You're aiming pretty low! A criteria below 40% might allow too many candidates through. Consider raising it to focus on those better suited to the role.";
    }
    if (value >= 40 && value <= 60) {
      return "This range is a good starting point. You'll catch candidates with a decent match, but don’t be afraid to adjust based on your specific needs.";
    }

    if (value > 60 && value <= 90) {
      return "Now you're getting selective! This range helps ensure candidates closely match your requirements, giving you a solid pool of potentials.";
    }

    if (value > 90) {
      return "Whoa, setting the bar high, aren’t we? With criteria this tough, only a handful of rockstars might make it through. Are you sure you're setting this criteria?";
    }
    return "";
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          margin: 0,
          p: 0,
          overflow: { xs: "auto", lg: "hidden" },
          maxWidth: "none",
          // height: "99vh",
          borderRadius: "15px",
          width: { xs: "98vw", lg: "82vw" },
        },
      }}
    >
      {!passingCriteriaSet ? (
        <Card
          sx={{
            m: 4,
            p: 2,
            height: "auto",
            borderRadius: "10px",
          }}
        >
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Invite candidates</Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ border: 1, borderColor: "#999" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body1" color="text.secondary" my={2}>
            Set the passing criteria for resumes and interviews to find
            potential candidates based on your requirements.
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            You can set criteria ranging from a minimum of 20% to a maximum of
            100% for both. This will be directly applied to the resume screening
            process and the interview process.
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Interview passing criteria
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Candidates are considered passed when their resumes match at least
              60% of the given requirements
            </Typography>

            <Box
              sx={{
                px: 5,
                borderRadius: 10,
                position: "relative",
                boxShadow: "0px 0px 20px 3px #0000000D",
              }}
            >
              <CustomSlider
                value={interviewValue}
                onChange={(_, newValue) => setInterviewValue(newValue)}
                marks={marks}
                min={20}
                max={99}
                sx={{ mt: 2 }}
                valueLabelDisplay="auto"
              />
              <SuggestedMarker suggestedValue={suggestedValue} />
            </Box>
            {/* Show message below the slider */}
            {interviewValue ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mx: 2,
                }}
              >
                <Box
                  component="img"
                  src={
                    interviewValue < 40
                      ? WARNINGICON
                      : interviewValue >= 40 && interviewValue <= 60
                      ? GOODICON
                      : interviewValue > 60 && interviewValue <= 90
                      ? GOODICON1 // ✅ Assign GOODICON for this range
                      : interviewValue > 90
                      ? CAUTIONICON
                      : null
                  }
                  mt={2}
                  height={25}
                />

                <Typography
                  variant="body2"
                  color={
                    interviewValue < 40
                      ? "#FFB400"
                      : interviewValue >= 40 && interviewValue <= 60
                      ? "#0BC6CF"
                      : interviewValue > 60 && interviewValue <= 90
                      ? "#9048FB" // ✅ Assign #9048FB for this range
                      : interviewValue > 90
                      ? "#FF5B5B"
                      : "black"
                  }
                  sx={{ mt: 2, ml: 2 }}
                >
                  {getMessageForValue(interviewValue)}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Card>
      ) : (
        <>
          <SelectLanguage modal2 onSelect={onSelect} onClose={onClose} />
        </>
      )}
      <DialogActions>
        <Box display="flex" justifyContent="flex-end" gap={2} mr={2} mb={2}>
          {!passingCriteriaSet && (
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="main"
                sx={{ bgcolor: "purple.main" }}
                onClick={() => {
                  setPassingCriteriaSet(true);
                }}
              >
                Confirm & continue
              </Button>
            </Box>
          )}

          {passingCriteriaSet && (
            <Button
              variant="main"
              sx={{ bgcolor: "purple.main" }}
              onClick={onConfirm}
              endIcon={
                actionLoading === "invite" && (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                )
              }
            >
              Confirm & continue
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SelectLanguageModal;
